import { ref } from 'vue';
import { httpService } from '@/services/httpService';
import { log } from '@/utils/DebugLogger';
import type { Usuarios_RegrasDeGruposResponse, Usuarios_RegrasDeGruposResponseDados } from '../contracts/Usuarios_RegrasDeGruposResponse';
import '../contracts/Usuarios_RegrasDeGruposResponse';
import type { Usuarios_OpcoesDeAtributoResponse } from '../contracts/Usuarios_OpcoesDeAtributoResponse';
import '../contracts/Usuarios_OpcoesDeAtributoResponse';
import type { OpcaoDeAcesso } from '@/domains/users/acesso/escoposDeAcesso';

/**
 * useRegrasDeGrupos — o que a tela de usuários sabe sobre os grupos SEM conhecer nome nenhum
 * (skill `app-groups`). Vem do app-api (GET /usuarios/regras_de_grupos), que por sua vez lê a
 * política do tenant: os grupos de app (`apps:` no tenant.yml), os atributos que cada grupo
 * destrava (o `unlocks:` dos grupos no scripts/doctor/curation.yml, resolvido contra o env do tenant).
 *
 * Nunca escreva um nome de grupo nas telas para decidir o que mostrar: pergunte aqui.
 * O servidor recusa do mesmo jeito — isto só esconde o que a pessoa não pode usar.
 */

const vazio = (): Usuarios_RegrasDeGruposResponseDados => ({ grupos_de_app: [], atributos: [], catalogo: [] });

// Uma carga por sessão, compartilhada pelas etapas do assistente.
const regras = ref<Usuarios_RegrasDeGruposResponseDados>(vazio());
let emVoo: Promise<void> | null = null;
let carregado = false;

export function useRegrasDeGrupos() {
  const carregar = (): Promise<void> => {
    if (carregado) return Promise.resolve();
    if (emVoo) return emVoo;
    emVoo = (async () => {
      try {
        const response: Usuarios_RegrasDeGruposResponse = await httpService.get('/usuarios/regras_de_grupos', {
          responseContract: 'Usuarios_RegrasDeGruposResponse'
        });
        if (response?.success === false || !response.dados) {
          throw new Error(typeof response?.error === 'string' && response.error ? response.error : 'regras_de_grupos_failed');
        }
        regras.value = response.dados;
        carregado = true;
      } catch (error) {
        // Sem regras, nenhum editor aparece e nenhum grupo é tratado como de app: o lado seguro.
        log('useRegrasDeGrupos', 'carregar', 'Falha ao carregar as regras de grupos', error);
        regras.value = vazio();
      } finally {
        emVoo = null;
      }
    })();
    return emVoo;
  };

  /**
   * O container de acesso deste atributo aparece para uma conta com estes grupos? Quando o tenant
   * o declara, quem edita pode editá-lo e a conta tem o grupo que o destrava.
   */
  const mostraAtributo = (atributo: string, grupos: readonly string[]): boolean => {
    const regra = regras.value.atributos.find((a) => a.atributo === atributo);
    return !!regra && regra.pode_editar === true && grupos.includes(regra.grupo);
  };

  /**
   * O atributo deve ser limpo para uma conta com estes grupos? Quando a conta deixou o grupo que o
   * destrava — e só se quem edita pode editá-lo (limpar também é mudar; o servidor recusaria).
   * O app-api também limpa ao tirar o grupo; isto mantém o formulário coerente.
   */
  const deveLimpar = (atributo: string, grupos: readonly string[]): boolean => {
    const regra = regras.value.atributos.find((a) => a.atributo === atributo);
    return !!regra && regra.pode_editar === true && !grupos.includes(regra.grupo);
  };

  /** A conta só tem grupos de app? Então não recebe franquia (e tem pelo menos um grupo). */
  const soGruposDeApp = (grupos: readonly string[]): boolean =>
    grupos.length > 0 && grupos.every((g) => regras.value.grupos_de_app.includes(g));

  return { regras, carregar, mostraAtributo, deveLimpar, soGruposDeApp, opcoesDoAtributo };
}

/**
 * As opções do atributo, do serviço que o possui (app-api repassa o GET /access-catalog dele).
 * Falha lança com o motivo — a tela mostra o erro, nunca uma lista vazia.
 */
async function opcoesDoAtributo(atributo: string): Promise<OpcaoDeAcesso[]> {
  const response: Usuarios_OpcoesDeAtributoResponse = await httpService.get(
    `/usuarios/opcoes_de_atributo/${encodeURIComponent(atributo)}`,
    { responseContract: 'Usuarios_OpcoesDeAtributoResponse' }
  );
  if (response?.success === false || !response.dados) {
    throw new Error(typeof response?.error === 'string' && response.error ? response.error : 'opcoes_de_atributo_failed');
  }
  return response.dados.opcoes.map((o) => ({
    id: o.id,
    rotulo: o.label,
    escopo: o.scope,
    descricao: o.description ?? undefined,
    grupo: o.group ?? undefined,
    sensivel: o.sensitive === true
  }));
}

/** Só para testes: esquece a carga (o estado é de módulo, compartilhado). */
export function resetRegrasDeGrupos(): void {
  regras.value = vazio();
  emVoo = null;
  carregado = false;
}
